import type { TFunction } from 'i18next';
import { U2000IdColumnDef, type U2000CriteriaStaticOption, type U2000TableColumn } from '../../u2000Core/components/tables/basic/U2000BasicTableList';
import {
  U2000_CRITERIA_LOOKUP_U3134_FIND_EMPLOYEE,
  U2000_CRITERIA_LOOKUP_U3134_FIND_INVOICE,
  U2000_CRITERIA_LOOKUP_U3134_FIND_PROJECT,
} from '../../u2000Core/components/tables/commons/search/U2000CriteriaLookupRegistry';
import { formatCurrency } from '../../u2000Core/tools/U2000CurrencyUtils';
import { u3125FormatInvoiceNumberDisplay } from '../../u3125Facture/U3125Service';
import { NS } from '../U3134';
import {
  U3134_TRANSACTION_KIND,
  type U3134TransactionKind,
  type U3134WorkInProgressSearchRow,
  u3134ComputeReportableAmount,
  u3134FormatTaskListLabel,
} from '../U3134Service';

const CURRENCY_DISPLAY_OPTS = {
  currencySymbol: '$',
  symbolPosition: 'left' as const,
  decimalSeparator: '.' as const,
  thousandSeparator: ' ' as const,
  decimalDigits: 2,
};

function parseNumericGridValue(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const n = Number(trimmed.replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }
  if (typeof value === 'object' && value !== null) {
    const o = value as Record<string, unknown>;
    if ('value' in o) return parseNumericGridValue(o.value);
  }
  return null;
}

const U3134_TRANSACTION_KIND_CRITERIA_VALUES: readonly U3134TransactionKind[] = [
  U3134_TRANSACTION_KIND.Honorarium,
  U3134_TRANSACTION_KIND.Expense,
  U3134_TRANSACTION_KIND.Equipment,
];

export function u3134BuildTransactionKindCriteriaOptions(t: TFunction): U2000CriteriaStaticOption[] {
  return U3134_TRANSACTION_KIND_CRITERIA_VALUES.map((value) => ({
    value,
    label: t(`lblTransactionKind_${value}`, { ns: NS, defaultValue: value }),
  }));
}

/** Colonnes liste / critères U3134 (BL-0397, BL-0655 tâche FT). */
export function u3134GetWorkInProgressListColumns(t: TFunction, language = 'fr'): U2000TableColumn[] {
  const transactionKindOptions = u3134BuildTransactionKindCriteriaOptions(t);
  return [
    U2000IdColumnDef,
    {
      name: 'project.projetNumber',
      label: t('colProject', { ns: NS }),
      widthClass: 'w-[220px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
      includeConditions: ['equals', 'in'],
      criteriaLookup: {
        findMethod: U2000_CRITERIA_LOOKUP_U3134_FIND_PROJECT,
        searchCriterionField: 'projetNumber',
        valueKey: 'projetNumber',
        criteriaField: 'project.projetNumber',
        displayKeys: ['projetNumber', 'projetName'],
        selectedDisplayKeys: ['projetNumber'],
        placeholder: t('phCriteriaProject', { ns: 'common' }),
      },
      display: (_column, _value, row) => {
        const p = (row as U3134WorkInProgressSearchRow).project;
        if (!p) return '';
        const num = p.projetNumber?.trim() ?? '';
        const name = p.projetName?.trim() ?? '';
        if (num && name) return `${num} — ${name}`;
        return num || name || p.id || '';
      },
    },
    {
      name: 'employee',
      label: t('lblEmployee', { ns: NS }),
      widthClass: 'w-[180px]',
      type: 'string',
      align: 'left',
      isCriteriaField: false,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
      display: (_column, _value, row) => {
        const emp = (row as U3134WorkInProgressSearchRow).employee;
        if (!emp) return '';
        const parts = [emp.firstName, emp.lastName].filter(Boolean);
        return parts.length ? parts.join(' ') : (emp.codeEmployee ?? '');
      },
    },
    {
      name: 'codeEmployee',
      label: t('lblEmployee', { ns: NS }),
      widthClass: 'w-[120px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: false,
      isDefaultColumn: false,
      sortable: true,
      includeConditions: ['equals', 'in'],
      criteriaLookup: {
        findMethod: U2000_CRITERIA_LOOKUP_U3134_FIND_EMPLOYEE,
        searchCriterionField: 'codeEmployee',
        valueKey: 'codeEmployee',
        criteriaField: 'codeEmployee',
        displayKeys: ['codeEmployee', 'firstName', 'lastName'],
        selectedDisplayKeys: ['codeEmployee', 'firstName', 'lastName'],
        placeholder: t('phCriteriaEmployee', { ns: 'common' }),
      },
    },
    {
      name: 'invoice.invoiceNumber',
      label: t('lblInvoice', { ns: NS }),
      widthClass: 'w-[100px]',
      type: 'string',
      align: 'right',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
      includeConditions: ['equals', 'in', 'isNull', 'isNotNull'],
      criteriaLookup: {
        findMethod: U2000_CRITERIA_LOOKUP_U3134_FIND_INVOICE,
        searchCriterionField: 'invoiceNumber',
        valueKey: 'invoiceNumber',
        displayKeys: ['invoiceNumber'],
        placeholder: t('phCriteriaInvoice', { ns: 'common' }),
      },
      display: (_column, value, row) => {
        const inv = (row as U3134WorkInProgressSearchRow).invoice;
        const draftHint = t('lblInvoiceNumberDraft', { ns: NS });
        return u3125FormatInvoiceNumberDisplay(
          (value as string | null | undefined) ?? inv?.invoiceNumber,
          draftHint,
        );
      },
    },
    {
      name: 'transactionKind',
      label: t('colTransactionKind', { ns: NS }),
      widthClass: 'w-[120px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
      includeConditions: ['equals', 'in'],
      criteriaStaticOptions: transactionKindOptions,
      display: (_column, value) => {
        const v = value != null ? String(value) : '';
        if (!v) return '';
        return t(`lblTransactionKind_${v}`, { ns: NS, defaultValue: v });
      },
    },
    {
      name: 'transactionDate',
      label: t('colTransactionDate', { ns: NS }),
      widthClass: 'w-[160px]',
      type: 'date',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: true,
    },
    {
      name: 'task',
      label: t('colTaskFt', { ns: NS }),
      widthClass: 'w-[220px]',
      type: 'string',
      align: 'left',
      isCriteriaField: false,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
      display: (_column, _value, row) =>
        u3134FormatTaskListLabel((row as U3134WorkInProgressSearchRow).task, language),
    },
    {
      name: 'amount',
      label: t('colAmount', { ns: NS }),
      widthClass: 'w-[120px]',
      type: 'number',
      align: 'right',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
      display: (_column, _value, row) => {
        const r = row as U3134WorkInProgressSearchRow;
        const n = parseNumericGridValue(r.reportableAmount ?? u3134ComputeReportableAmount(r));
        if (n === null) return null;
        return formatCurrency(n, CURRENCY_DISPLAY_OPTS);
      },
    },
    {
      name: 'billable',
      label: t('lblBillable', { ns: NS }),
      widthClass: 'w-[90px]',
      type: 'boolean',
      align: 'center',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
      display: (_column, value) =>
        value === true || value === 'true'
          ? t('lblYes', { ns: NS })
          : value === false || value === 'false'
            ? t('lblNo', { ns: NS })
            : '',
    },
    {
      name: 'remark',
      label: t('colRemark', { ns: NS }),
      widthClass: 'w-[200px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: false,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: 'createdAt',
      label: t('lblFldCreatedAt', { ns: NS }),
      widthClass: 'w-[150px]',
      isDefaultColumn: true,
      type: 'date',
      inGridField: true,
      sortable: true,
      includeConditions: [
        'equals',
        'notequals',
        'greaterthan',
        'greaterthanorequal',
        'lesthan',
        'lesthanorequal',
        'interval',
      ],
      isCriteriaField: true,
    },
    {
      name: 'deletedAt',
      label: t('lblCndFieldDeletedAt', { ns: NS }),
      widthClass: 'w-[250px]',
      type: 'date',
      inGridField: true,
      align: 'center',
      display: (_column, value) => {
        return value ? <span className="font-bold text-red-600">{t('lblRowSoftDeleted', { ns: NS })}</span> : '';
      },
      includeConditions: ['isNotNull'],
      ovverideLabelConditions: [
        {
          name: 'isNotNull',
          label: t('lblCndDeletedAt', { ns: NS }),
        },
      ],
      isCriteriaField: true,
    },
  ];
}
